import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  fetchShopList,
  fetchShopConfig,
  fetchMonthlyAvailability,
  createBooking
} from "../api.js";
import ShopSelect from "../components/ShopSelect.jsx";
import HairdresserSelect from "../components/HairdresserSelect.jsx";
import AppointmentTypeSelect from "../components/AppointmentTypeSelect.jsx";
import SlotSelect from "../components/SlotSelect.jsx";
import ClientForm from "../components/ClientForm.jsx";
import Confirmation from "../components/Confirmation.jsx";
import { ArrowLeft, Loader2 } from "lucide-react";

const STEPS = {
  SHOP: 0,
  HAIRDRESSER: 1,
  TYPE: 2,
  SLOT: 3,
  CLIENT: 4,
  DONE: 5
};

export default function BookingPage() {
  const { orgSlug } = useParams();
  const [config, setConfig] = useState(null);
  const [shops, setShops] = useState([]);
  const [selectedShop, setSelectedShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [configLoading, setConfigLoading] = useState(false);
  const [error, setError] = useState(null);
  const today = useMemo(() => new Date(), []);
  const minMonth = today.getMonth() + 1;
  const minYear = today.getFullYear();
  const maxDate = useMemo(() => {
    const next = new Date(today);
    next.setFullYear(next.getFullYear() + 1);
    return next;
  }, [today]);
  const maxMonth = maxDate.getMonth() + 1;
  const maxYear = maxDate.getFullYear();

  const [step, setStep] = useState(STEPS.SHOP);
  const [hairdresser, setHairdresser] = useState(null);
  const [appointmentType, setAppointmentType] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(() => minMonth);
  const [calendarYear, setCalendarYear] = useState(() => minYear);
  const [availabilityByDay, setAvailabilityByDay] = useState({});
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedDate, setSelectedDate] = useState("");
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [slotError, setSlotError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [booking, setBooking] = useState(null);
  const availableAppointmentTypes = useMemo(() => {
    if (!hairdresser) return [];
    return hairdresser.appointment_types || [];
  }, [hairdresser]);

  const resetSlotSelection = useCallback(() => {
    setCalendarMonth(minMonth);
    setCalendarYear(minYear);
    setAvailabilityByDay({});
    setSelectedDay(null);
    setSelectedDate("");
    setSelectedSlot(null);
    setSlotError(null);
  }, [minMonth, minYear]);

  const resetFlow = useCallback(() => {
    setHairdresser(null);
    setAppointmentType(null);
    resetSlotSelection();
    setBooking(null);
  }, [resetSlotSelection]);

  const loadShopConfig = useCallback(async (shop) => {
    setConfigLoading(true);
    setError(null);
    setSelectedShop(shop);
    resetFlow();
    try {
      const data = await fetchShopConfig(orgSlug, shop.id);
      const hasHairdressers = (data.hairdressers || []).length > 0;
      const hasTypes = (data.hairdressers || []).some(
        (h) => (h.appointment_types || []).length > 0
      );
      if (!hasHairdressers || !hasTypes) {
        setConfig(null);
        setSelectedShop(null);
        setStep(STEPS.SHOP);
        setError("O salão não está configurado. Contacte o administrador.");
        return;
      }
      setConfig(data);
      setStep(STEPS.HAIRDRESSER);
    } catch (err) {
      setConfig(null);
      setSelectedShop(null);
      setStep(STEPS.SHOP);
      const isNotFound = err?.response?.status === 404;
      setError(isNotFound ? "Salão não encontrado." : "Erro ao carregar configuração.");
    } finally {
      setConfigLoading(false);
    }
  }, [orgSlug, resetFlow]);

  useEffect(() => {
    fetchShopList(orgSlug)
      .then((data) => {
        setShops(data);
        if (data.length === 1) {
          loadShopConfig(data[0]);
        } else {
          setStep(STEPS.SHOP);
        }
        setLoading(false);
      })
      .catch((err) => {
        const isNoShopConfigured =
          err?.response?.status === 404 &&
          err?.response?.data?.detail === "No shop configured";
        setError(
          isNoShopConfigured
            ? "O salão não está configurado. Contacte o administrador."
            : "Erro ao carregar configuração."
        );
        setLoading(false);
      });
  }, [orgSlug, loadShopConfig]);

  useEffect(() => {
    if (step !== STEPS.SLOT || !config || !hairdresser || !appointmentType) return;
    setSlotsLoading(true);
    setSlotError(null);
    fetchMonthlyAvailability(
      orgSlug,
      config?.shop_id,
      hairdresser.id,
      appointmentType.id,
      calendarMonth,
      calendarYear
    )
      .then((data) => {
        setAvailabilityByDay(data.days || {});
      })
      .catch(() => {
        setAvailabilityByDay({});
        setSlotError("Erro ao carregar disponibilidade.");
      })
      .finally(() => {
        setSlotsLoading(false);
      });
  }, [orgSlug, step, config, hairdresser, appointmentType, calendarMonth, calendarYear]);

  function formatDate(year, month, day) {
    const paddedMonth = String(month).padStart(2, "0");
    const paddedDay = String(day).padStart(2, "0");
    return `${year}-${paddedMonth}-${paddedDay}`;
  }

  function handleHairdresserSelect(h) {
    setHairdresser(h);
    setAppointmentType(null);
    resetSlotSelection();
    setStep(STEPS.TYPE);
  }

  function handleTypeSelect(t) {
    setAppointmentType(t);
    resetSlotSelection();
    setStep(STEPS.SLOT);
  }

  function handleMonthChange(nextMonth, nextYear) {
    setCalendarMonth(nextMonth);
    setCalendarYear(nextYear);
    setAvailabilityByDay({});
    setSelectedDay(null);
    setSelectedDate("");
    setSelectedSlot(null);
    setSlotError(null);
  }

  function handleDaySelect(day) {
    setSelectedDay(day);
    setSelectedDate(formatDate(calendarYear, calendarMonth, day));
    setSelectedSlot(null);
    setSlotError(null);
  }

  function handleSlotSelect(slot) {
    if (!selectedDate) {
      setSlotError("Selecione um dia disponível primeiro.");
      return;
    }
    setSelectedSlot(slot);
    setStep(STEPS.CLIENT);
  }

  function handleBack() {
    setError(null);
    if (step === STEPS.HAIRDRESSER) {
      setSelectedShop(null);
      setConfig(null);
      resetFlow();
      setStep(STEPS.SHOP);
      return;
    }
    if (step === STEPS.TYPE) {
      setHairdresser(null);
      setAppointmentType(null);
      resetSlotSelection();
      setBooking(null);
      setStep(STEPS.HAIRDRESSER);
      return;
    }
    if (step === STEPS.SLOT) {
      setAppointmentType(null);
      resetSlotSelection();
      setBooking(null);
      setStep(STEPS.TYPE);
      return;
    }
    if (step === STEPS.CLIENT) {
      setSelectedSlot(null);
      setSlotError(null);
      setBooking(null);
      setStep(STEPS.SLOT);
    }
  }

  const showBack =
    (step === STEPS.HAIRDRESSER && shops.length > 1) ||
    (step > STEPS.HAIRDRESSER && step < STEPS.DONE);

  async function handleClientSubmit(clientData) {
    setSubmitting(true);
    try {
      const result = await createBooking(orgSlug, {
        shop_id: config.shop_id,
        hairdresser_id: hairdresser.id,
        appointment_type_id: appointmentType.id,
        date: selectedDate,
        start_minute: selectedSlot.start_minute,
        ...clientData
      });
      setBooking({
        ...result,
        hairdresser_name: hairdresser.name,
        appointment_type_name: appointmentType.name
      });
      setStep(STEPS.DONE);
      setError(null);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        setError("O horário selecionado já não está disponível. Por favor, escolha outro.");
        setSelectedSlot(null);
        setStep(STEPS.SLOT);
      } else if (status === 400) {
        setError("O horário selecionado é inválido. Por favor, escolha outro.");
        setSelectedSlot(null);
        setStep(STEPS.SLOT);
      } else if (status === 422) {
        const detail = err?.response?.data?.detail;
        if (Array.isArray(detail)) {
          const hasEmailError = detail.some(
            (e) => Array.isArray(e.loc) && e.loc.includes("client_email")
          );
          if (hasEmailError) {
            setError("O email introduzido não é válido. Por favor, corrija e tente novamente.");
          } else {
            setError("Dados inválidos. Por favor, verifique os campos e tente novamente.");
          }
        } else {
          setError("Dados inválidos. Por favor, verifique os campos e tente novamente.");
        }
      } else {
        setError("Erro ao criar marcação. Tente novamente.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (loading || configLoading) {
    return (
      <main className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-gold animate-spin" />
      </main>
    );
  }

  if (error && !config && shops.length <= 1) {
    return (
      <main className="min-h-screen bg-background p-6 md:p-12">
        <p className="text-red-400 text-center">{error}</p>
      </main>
    );
  }

  const getStepTitle = () => {
    if (step === STEPS.SHOP) return "Escolha o salão para continuar.";
    if (step === STEPS.HAIRDRESSER) return "Escolha o profissional";
    if (step === STEPS.TYPE) return "Escolha o serviço";
    if (step === STEPS.SLOT) return "Escolha o dia e horário";
    if (step === STEPS.CLIENT) return "Os seus dados";
    if (step === STEPS.DONE) return "Marcação confirmada";
    return "";
  };

  return (
    <main className="min-h-screen bg-background p-6 md:p-12">
      <div className="max-w-2xl mx-auto">
        {showBack && (
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-white mb-6 hover:text-gold transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Voltar</span>
          </button>
        )}

        <header className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gold mb-3">
            Agendamento de Cabeleireiro
          </h1>
          <p className="text-muted text-lg">
            {getStepTitle()}
          </p>
        </header>

        {error && (
          <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-lg mb-6">
            {error}
          </div>
        )}

      {step === STEPS.SHOP && shops.length > 1 && (
        <ShopSelect
          shops={shops}
          onSelect={loadShopConfig}
        />
      )}

      {step === STEPS.HAIRDRESSER && config && (
        <HairdresserSelect
          hairdressers={config.hairdressers}
          onSelect={handleHairdresserSelect}
        />
      )}

      {step === STEPS.TYPE && config && (
        <AppointmentTypeSelect
          types={availableAppointmentTypes}
          onSelect={handleTypeSelect}
        />
      )}

      {step === STEPS.SLOT && config && (
        <SlotSelect
          month={calendarMonth}
          year={calendarYear}
          minMonth={minMonth}
          minYear={minYear}
          maxMonth={maxMonth}
          maxYear={maxYear}
          availability={availabilityByDay}
          selectedDay={selectedDay}
          onMonthChange={handleMonthChange}
          onDaySelect={handleDaySelect}
          onSlotSelect={handleSlotSelect}
          loading={slotsLoading}
          error={slotError}
        />
      )}

      {step === STEPS.CLIENT && (
        <ClientForm onSubmit={handleClientSubmit} submitting={submitting} />
      )}

      {step === STEPS.DONE && booking && (
        <Confirmation
          booking={booking}
          onNewBooking={() => {
            resetFlow();
            setStep(shops.length > 1 ? STEPS.SHOP : STEPS.HAIRDRESSER);
          }}
        />
      )}
      </div>
    </main>
  );
}
